import type { ReactNode } from 'react';
import { AlertTriangle, Check, Clock, OctagonAlert } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { descreverErro, ehErroTentavel } from '@/lib/erro-api';
import { formatarFracaoPercentual, formatarMarkup } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { AvisoMotor } from './tipos';

function Cartao({ icone, titulo, tom = 'neutro', children, ...rest }: { icone: ReactNode; titulo: string; tom?: 'neutro' | 'aviso' | 'erro'; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn(
        'flex flex-[1_1_320px] items-start gap-3 rounded-xl border bg-card px-4 py-3.5',
        tom === 'neutro' && 'border-border',
        tom === 'aviso' && 'border-warning-dot/40 bg-warning-bg',
        tom === 'erro' && 'border-destructive/30 bg-danger-bg',
      )}
    >
      <span aria-hidden="true" className="mt-px shrink-0">
        {icone}
      </span>
      <div>
        <div className="font-semibold">{titulo}</div>
        <div className="text-small text-ink-secondary">{children}</div>
      </div>
    </div>
  );
}

const TITULO_AVISO: Record<string, string> = {
  DIVISOR_HIPERSENSIVEL: 'Preço muito sensível às taxas',
};

export interface AvisosFormacaoProps {
  /** warnings do motor (divisor etc.). */
  avisos: AvisoMotor[];
  /** Fracao do preco que sobra para o custo (= divisor), como veio da API. */
  custoFracao: string;
  margemSugerido: string;
  margemPraticado: string | null;
}

/** Cartoes de leitura do resultado: avisos do motor, margem de seguranca e margem a prazo. So texto da API. */
export function AvisosFormacao({ avisos, custoFracao, margemSugerido, margemPraticado }: AvisosFormacaoProps) {
  const sensivel = avisos.some((a) => a.code === 'DIVISOR_HIPERSENSIVEL');
  const outros = avisos.filter((a) => a.code !== 'DIVISOR_HIPERSENSIVEL');
  return (
    <div className="flex flex-wrap gap-3" data-testid="avisos-formacao">
      {sensivel ? (
        avisos
          .filter((a) => a.code === 'DIVISOR_HIPERSENSIVEL')
          .map((a) => (
            <Cartao key={a.code} role="alert" tom="aviso" titulo={TITULO_AVISO[a.code] ?? 'Aviso do motor'} icone={<AlertTriangle className="size-[18px] text-warning-ink" />}>
              {a.message} Sobram {formatarFracaoPercentual(custoFracao)} do preço para o custo.
            </Cartao>
          ))
      ) : (
        <Cartao titulo="Margem de segurança sem alerta" icone={<Check className="size-[18px] text-success" />}>
          Sobram {formatarFracaoPercentual(custoFracao)} do preço para o custo (divisor {formatarMarkup(custoFracao)}). O motor não
          apontou sensibilidade alta às taxas.
        </Cartao>
      )}
      {outros.map((a) => (
        <Cartao key={a.code} role="alert" tom="aviso" titulo={TITULO_AVISO[a.code] ?? 'Aviso do motor'} icone={<AlertTriangle className="size-[18px] text-warning-ink" />}>
          {a.message}
        </Cartao>
      ))}
      <Cartao titulo="A prazo, a margem é um pouco menor" icone={<Clock className="size-[18px] text-ink-secondary" />}>
        As despesas variáveis também incidem sobre o custo financeiro. Lucro efetivo a prazo: sugerido{' '}
        <b className="tabular text-ink">{formatarFracaoPercentual(margemSugerido)}</b>
        {margemPraticado !== null && (
          <>
            , praticado <b className="tabular text-ink">{formatarFracaoPercentual(margemPraticado)}</b>
          </>
        )}
        .
      </Cartao>
    </div>
  );
}

const TITULO_ERRO: Record<string, string> = {
  DIVISOR_NAO_POSITIVO: 'As taxas somam 100% ou mais',
  DADOS_AUSENTES: 'Faltam dados para calcular',
  PAYMENT_CONDITION_NOT_IN_PRICE_TABLE: 'Condição de pagamento fora da tabela',
  PAYMENT_CONDITION_INVALID: 'Condição de pagamento inválida',
  VALOR_INVALIDO: 'Valor inválido',
  // Regras por tipo de item (Sessao 6): a API recusa; a tela diz o motivo com o code visivel.
  FICHA_TIPO_NAO_PERMITIDO: 'Este tipo de item não tem ficha técnica',
  AQUISICAO_TIPO_NAO_PERMITIDO: 'Este tipo de item não tem custo de aquisição',
  COMPONENTE_NAO_PERMITIDO: 'Este item não pode ser componente de ficha',
  ITEM_USADO_EM_FICHAS: 'Item usado em fichas técnicas',
  NBS_SO_SERVICO: 'NBS só existe para serviço',
  FORNECEDOR_INATIVO: 'Fornecedor inativo',
  ITEM_NAO_COMERCIAVEL: 'Item não comerciável',
};

/**
 * Erro de carregamento ou de regra em linguagem clara (ver `descreverErro`), com uma linha de detalhe tecnico
 * (HTTP e code) para o suporte. `titulo` e o titulo da tela: usado nos erros de regra e no 403 sem lista de permissoes.
 */
export function ErroFormacao({ erro, onTentar, titulo: tituloFixo }: { erro: unknown; onTentar?: () => void; /** Titulo da tela (troca o padrao, que fala em "calcular"). */ titulo?: string }) {
  const api = erro instanceof ApiError ? erro : null;
  const descricao = descreverErro(erro);
  const semListaDePermissoes = api?.status === 403 && !(api.body?.permissoesFaltantes?.length);
  // Titulo: o da tela quando a tela informa (regra, 403 sem lista, 5xx, 404, rede); a permissao faltante no 403 com lista.
  let titulo = tituloFixo ?? descricao.titulo;
  let mensagem = descricao.mensagem;
  if (api?.status === 403 && !semListaDePermissoes) titulo = descricao.titulo;
  else if (api && (api.status === 400 || api.status === 409 || api.status === 422)) {
    titulo = tituloFixo ?? (api.code ? (TITULO_ERRO[api.code] ?? 'Não foi possível calcular') : descricao.titulo);
  } else if (tituloFixo && api?.status !== 403) {
    // Erro generico (5xx, 404, 401, rede): o titulo da tela segue; o da lib vai antes da mensagem.
    mensagem = `${descricao.titulo}. ${descricao.mensagem}`;
  }
  return (
    <div role="alert" className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3.5">
      <OctagonAlert aria-hidden="true" className="mt-px size-[18px] shrink-0 text-danger-ink" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-danger-ink">{titulo}</div>
        <div className="text-small text-ink-secondary">{mensagem}</div>
        {descricao.itens.length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-small text-ink-secondary">
            {descricao.itens.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        )}
        <div className="tabular mt-1 text-caption text-ink-muted">{descricao.detalhe}</div>
      </div>
      {onTentar && ehErroTentavel(erro) && (
        <button type="button" onClick={onTentar} className="h-8 rounded-lg border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
          Tentar de novo
        </button>
      )}
    </div>
  );
}
